// Pure helpers for the admin program screen (P11, plan §7.7). They only read the answers of
// GET /api/cms/events/{eventId}/program (heartscms program.ProgramView) and of the People area's
// person list; nothing here decides anything the backend already decided (`openSpot`, `removedAt`).
// The text pickers are shared with the public program so both views read DE/EN the same way.
import { formatTime, localDate, pick, timeRange } from '../program/programModel';

// pick/formatTime/timeRange/localDate are the public program's helpers, re-used so both views read
// DE/EN and times the same way.
export { localDate, pick, timeRange };

// Roles in the order the admin reads them (chairs first, same as the public view); the roles an
// entry actually carries are grouped in this order.
// The six states of an assignment (AssignmentStatus). Colour is never the only signal: every state
// is printed as a word next to its tag and carries its own glyph and left border, so it stays
// readable for colour-blind users. Contrast of fg on bg is >= 6:1 (WCAG AA needs 4.5:1).
export const STATUS_META = {
  PROPOSED: { id: 'programAdmin.status.PROPOSED', def: 'Not asked yet', bg: '#f1f5f9', fg: '#334155', border: '#94a3b8' },
  INVITED: { id: 'programAdmin.status.INVITED', def: 'Invited', bg: '#ede9fe', fg: '#5b21b6', border: '#7c3aed' },
  CONFIRMED: { id: 'programAdmin.status.CONFIRMED', def: 'Confirmed', bg: '#dcfce7', fg: '#166534', border: '#15803d' },
  DECLINED: { id: 'programAdmin.status.DECLINED', def: 'Declined', bg: '#fee2e2', fg: '#991b1b', border: '#b91c1c' },
  WITHDRAWN: { id: 'programAdmin.status.WITHDRAWN', def: 'Withdrawn', bg: '#ffedd5', fg: '#9a3412', border: '#c2410c' },
  CANCELLED: { id: 'programAdmin.status.CANCELLED', def: 'Cancelled', bg: '#e2e8f0', fg: '#475569', border: '#64748b' },
};

export const statusMeta = (status) => STATUS_META[status] || STATUS_META.PROPOSED;

export const roleLabelId = (role) => `programAdmin.role.${role}`;

// The state of the spot as the admin reads it: an open spot is what needs filling, whichever of the
// two reasons applies (nobody on it, or the person is gone).
export const isOpenSpot = (a) => (a.openSpot != null
  ? !!a.openSpot
  : a.removedAt == null && (a.personId == null || ['DECLINED', 'WITHDRAWN', 'CANCELLED'].includes(a.status)));

// A declined spot whose invitation carried a suggested replacement (plan §7.6: the suggestion lives
// on the invitation that answered, so a re-invite does not lose it).
export const declineSuggestionOf = (a) => {
  const declined = historyOf(a).filter((h) => h.declineReplacementName || h.declineReplacementEmail).pop();
  if (!declined) return null;
  return { name: declined.declineReplacementName, email: declined.declineReplacementEmail, askedAt: declined.closedAt };
};

// The suggestion that is still waiting for the admin: the spot is DECLINED, not removed, and the
// invitation that closed it (the last one, as SuggestedPersonService reads it) carries the name or
// the email. A re-invited, re-filled or removed spot has no such suggestion any more.
export const pendingSuggestionOf = (a) => {
  if (a.removedAt || a.status !== 'DECLINED') return null;
  const last = historyOf(a).pop();
  if (!last || !(last.declineReplacementName || last.declineReplacementEmail)) return null;
  return declineSuggestionOf(a);
};

// The person the suggestion points at by email, if they are already among this event's people: the
// side panel names them on its button. The real matching (email, name, similar names, among all
// persons) is the backend's plan, which the confirm dialog shows (SuggestedPersonService).
export const suggestedPersonOf = (a, people) => {
  const suggestion = declineSuggestionOf(a);
  if (!suggestion?.email) return null;
  const email = suggestion.email.toLowerCase();
  return (people || []).find((p) => (p.contactEmail || '').toLowerCase() === email) || null;
};

// Every invitation of the spot, oldest first (AssignmentHistory.Asked); what the public shape calls
// history, the admin calls the trail of who was asked.
export const historyOf = (a) => (a.history || []).slice().sort((x, y) => String(x.askedAt).localeCompare(String(y.askedAt)));
export const openInvitationOf = (a) => historyOf(a).find((h) => !h.closedAt) || null;

// Who asked (AssignmentHistory.Asked.askedByName, the sender's program name): the newest invitation
// that has one. Null for a cutover row or a send without a recorded account - the screen then shows
// no line at all.
export const invitedByOf = (a) => {
  const rows = historyOf(a);
  for (let i = rows.length - 1; i >= 0; i -= 1) {
    if (rows[i].askedByName) return rows[i].askedByName;
  }
  return null;
};

export const isEntry = (node) => !!node && node.type === 'ENTRY';
export const isElement = (node) => !!node && node.type === 'DISPLAY_ELEMENT';

const bySort = (a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0);

// ---------------------------------------------------------------- what the backend allows

// The state machine of one spot (AssignmentTransitions), as the screen needs it: which action the
// admin may take in which state. `neverAsked` is the fixture/API fact behind the move rule (the
// invitations of a spot are its history; nobody asked means no history row).
//
//   invite   PROPOSED, DECLINED, WITHDRAWN or CANCELLED, with a person (re-invite is allowed; an
//            INVITED spot is reminded, a CONFIRMED one never invited, an open spot never invited)
//   remind   INVITED (the reminder itself is per person and event, see reminderStateOf)
//   uninvite INVITED -> CANCELLED
//   decline  INVITED (the admin records what the person said, D18)
//   withdraw CONFIRMED -> WITHDRAWN
//   replace  everything but CONFIRMED (from INVITED the open invitation is cancelled first)
//   remove   everything but CONFIRMED (an INVITED one is uninvited first)
//   move     PROPOSED and nobody was ever asked about it
// A removed spot has no action at all, and neither has a spot of a removed entry.
export const wasAsked = (a) => (a.history || []).length > 0;

// The states an invitation (or a re-invitation, D10) may start from.
export const INVITE_FROM = ['PROPOSED', 'DECLINED', 'WITHDRAWN', 'CANCELLED'];

export const canInvite = (a) => !a.removedAt && a.personId != null && INVITE_FROM.includes(a.status);
export const canRemind = (a) => !a.removedAt && a.personId != null && a.status === 'INVITED';
export const canUninvite = (a) => !a.removedAt && a.status === 'INVITED';
export const canRecordDecline = (a) => !a.removedAt && a.status === 'INVITED';
export const canWithdraw = (a) => !a.removedAt && a.status === 'CONFIRMED';
export const canReplace = (a) => !a.removedAt && a.status !== 'CONFIRMED';
export const canRemoveSpot = (a) => !a.removedAt && a.status !== 'CONFIRMED';
export const canMoveSpot = (a) => !a.removedAt && a.status === 'PROPOSED' && !wasAsked(a);

// The actions of one spot, for the row and the panel. `remindersStoppedByPerson` is not part of it:
// a reminder is per person, the panel asks reminderStateFor for that.
export const spotCapabilities = (a, entryRemoved = false) => {
  const blocked = !!a.removedAt || !!entryRemoved;
  return {
    removed: blocked,
    invite: !blocked && canInvite(a),
    remind: !blocked && canRemind(a),
    uninvite: !blocked && canUninvite(a),
    decline: !blocked && canRecordDecline(a),
    withdraw: !blocked && canWithdraw(a),
    replace: !blocked && canReplace(a),
    remove: !blocked && canRemoveSpot(a),
    move: !blocked && canMoveSpot(a),
  };
};

// ---------------------------------------------------------------- the subtree of an entry

// Every spot at or below a node, with the entry it sits on - what the entry rules look at (plan §7.6:
// an entry's removal owns its whole subtree, not only its own spots).
export const subtreeSpots = (node) => {
  const out = [];
  const walk = (entry) => {
    (entry.assignments || []).forEach((a) => out.push({ assignment: a, entry }));
    (entry.children || []).forEach((child) => { if (isEntry(child)) walk(child); });
  };
  if (isEntry(node)) walk(node);
  return out;
};

// The first spot that forbids removing this entry: a CONFIRMED one anywhere in it (not removed).
export const confirmedInSubtree = (node) => subtreeSpots(node)
  .find(({ assignment }) => !assignment.removedAt && assignment.status === 'CONFIRMED') || null;

// The spots of the subtree whose open invitation a removal would cancel (one email per person).
export const invitedInSubtree = (node) => subtreeSpots(node)
  .filter(({ assignment }) => !assignment.removedAt && assignment.status === 'INVITED');

// Nobody was ever asked in the whole subtree: the removal deletes instead of removing (plan §7.6).
export const askedInSubtree = (node) => subtreeSpots(node).some(({ assignment }) => wasAsked(assignment));

export const canRemoveEntry = (entry) => !entry.removedAt && !confirmedInSubtree(entry);

// The entries a node can be swapped with, in the order they are printed: its parent's entry children,
// or - at the top level - the entries of its own day. The first has nothing above it, the last
// nothing below it (the "up"/"down" buttons).
export const siblingEntries = (program, entry, parent = null) => {
  if (entry.depth === 0) {
    return (program?.entries || [])
      .filter((n) => isEntry(n) && n.eventDayId === entry.eventDayId && !n.removedAt)
      .slice().sort(bySort);
  }
  return (parent?.children || []).filter((n) => isEntry(n) && !n.removedAt).slice().sort(bySort);
};

export const positionOf = (siblings, entry) => {
  const index = siblings.findIndex((e) => e.id === entry.id);
  return { index, count: siblings.length, first: index === 0, last: index === siblings.length - 1 };
};

const assignmentsOf = (entry) => (entry.assignments || []).filter((a) => !a.removedAt).slice().sort(bySort);
export const removedAssignmentsOf = (entry) => (entry.assignments || []).filter((a) => a.removedAt).slice().sort(bySort);
export const childrenOf = (node) => (node?.children || []);

// The quick filters of the Program area, one at a time (§7.7 item 4; owner 2026-10-21: the spot
// states the rows show - to fill, to invite (a person on it, not asked yet), invited, declined,
// confirmed; the earlier "needs attention" mix of open spots and invitations at the reminder
// maximum is gone).
export const SPOT_FILTERS = ['open', 'toInvite', 'invited', 'declined', 'confirmed'];

export const matchesSpotFilter = (a, filter) => {
  if (a.removedAt) return false;
  switch (filter) {
    case 'open': return isOpenSpot(a);
    case 'toInvite': return a.status === 'PROPOSED' && a.personId != null;
    case 'invited': return a.status === 'INVITED';
    case 'declined': return a.status === 'DECLINED';
    case 'confirmed': return a.status === 'CONFIRMED';
    default: return true;
  }
};

// The live spots of one entry, only those the filter keeps when one is on.
export const shownAssignmentsOf = (entry, filter, people) => assignmentsOf(entry)
  .filter((a) => !filter || matchesSpotFilter(a, filter, people));

// Whether anything at or below this node matches the filter - what keeps the filter from emptying a
// block that still holds a matching spot two levels down.
export const subtreeMatchesFilter = (node, filter, people) => {
  if (!isEntry(node)) return false;
  if ((node.assignments || []).some((a) => matchesSpotFilter(a, filter, people))) return true;
  return childrenOf(node).some((child) => subtreeMatchesFilter(child, filter, people));
};

// How many spots of the whole event each filter keeps.
export const countSpotFilters = (program, people) => {
  const counts = Object.fromEntries(SPOT_FILTERS.map((f) => [f, 0]));
  walkEntries(program, (entry) => {
    (entry.assignments || []).forEach((a) => {
      SPOT_FILTERS.forEach((f) => { if (matchesSpotFilter(a, f, people)) counts[f] += 1; });
    });
  });
  return counts;
};

// Every assignment of the tree, in reading order, with its entry - the flat list the counts, the
// People area and the selection index are built from.
const walkEntries = (program, visit, parent = null, depth = 0) => {
  (program?.entries || []).filter(isEntry).forEach((entry) => {
    visit(entry, parent, depth);
    const walk = (children, parentEntry, childDepth) => (children || []).forEach((n) => {
      if (!isEntry(n)) return;
      visit(n, parentEntry, childDepth);
      walk(n.children, n, childDepth + 1);
    });
    walk(entry.children, entry, depth + 1);
  });
};

// The display elements are indexed apart (their ids are another table's): each with the entry it
// hangs off, null on the top level of a day.
export const indexProgram = (program) => {
  const entries = new Map();
  const spots = new Map();
  const elements = new Map();
  (program?.entries || []).filter(isElement).forEach((element) => elements.set(element.id, { element, parent: null }));
  walkEntries(program, (entry, parent, depth) => {
    entries.set(entry.id, { entry, parent, depth });
    (entry.assignments || []).forEach((a) => spots.set(a.id, { assignment: a, entry }));
    childrenOf(entry).filter(isElement).forEach((element) => elements.set(element.id, { element, parent: entry }));
  });
  return { entries, spots, elements };
};

// ---------------------------------------------------------------- display elements

// ProgramDisplayElement's kinds (DisplayKind) and the one style the public program, the PDF and the
// import know (ProgramDay, ImportPlanner): a highlighted box.
export const DISPLAY_KINDS = ['TEXT', 'QUOTE', 'HEADING'];
// Their English names, the default messages of programAdmin.element.kind.* (de.json has the German).
export const DISPLAY_KIND_NAMES = { TEXT: 'Text', QUOTE: 'Quote', HEADING: 'Heading' };
export const HIGHLIGHTED_BOX = 'HIGHLIGHTED_BOX';

// Whether an element sits in a removed entry (or below one): such an element is history only.
export const elementInRemoved = (index, elementId) => {
  let parent = index.elements.get(elementId)?.parent || null;
  while (parent) {
    if (parent.removedAt) return true;
    parent = index.entries.get(parent.id)?.parent || null;
  }
  return false;
};

// The live siblings a new display element is placed among, in the order ProgramService.reorder
// counts them: the entries (removed ones take no part) and display elements of one entry, or of the
// top level of one day, by sort order, an entry before an element at an equal one, then by id. The
// `position` of the create is an index into this list: 0 is the start, i + 1 is right after the
// i-th sibling, null is the end.
const bySequence = (a, b) => ((a.sortOrder ?? 0) - (b.sortOrder ?? 0))
  || ((isEntry(a) ? 0 : 1) - (isEntry(b) ? 0 : 1))
  || (a.id - b.id);

export const placeSiblings = (program, parent, dayId) => (parent
  ? childrenOf(parent)
  : (program?.entries || []).filter((n) => n.eventDayId === dayId))
  .filter((n) => !n.removedAt)
  .slice()
  .sort(bySequence);

// A person's live spots at this event, in reading order, with their entry (the person panel's list).
export const spotsOfPerson = (program, personId) => {
  const out = [];
  walkEntries(program, (entry) => {
    (entry.assignments || []).forEach((a) => { if (a.personId === personId && !a.removedAt) out.push({ assignment: a, entry }); });
  });
  return out;
};

export const spotCount = (program) => {
  let count = 0;
  walkEntries(program, (entry) => { count += (entry.assignments || []).length; });
  return count;
};

// ---------------------------------------------------------------- the day's timeline

const nodesOfDay = (program, dayId) => (program?.entries || [])
  .filter((n) => n.eventDayId === dayId)
  .slice()
  .sort(bySort);

// The same layout the public timeline uses (a time column, parallel blocks in one slot, untimed
// blocks per room). Removed nodes are returned separately so the screen can put them behind the
// "show removed" switch at the end of their day instead of in the middle of the timetable.
export const layoutDay = (program, dayId) => {
  const all = nodesOfDay(program, dayId);
  const live = all.filter((n) => n.removedAt == null);
  const elements = live.filter(isElement);
  const entries = live.filter(isEntry);

  // A top-level display element sits right after the entry that precedes it in the day's shared
  // sort_order sequence (design §4.4.1); the one without a predecessor opens the day.
  const after = new Map();
  const before = [];
  elements.forEach((el) => {
    let anchor = null;
    entries.forEach((e) => {
      if (e.sortOrder < el.sortOrder && (anchor == null || e.sortOrder > anchor.sortOrder)) anchor = e;
    });
    if (!anchor) before.push(el);
    else {
      if (!after.has(anchor.id)) after.set(anchor.id, []);
      after.get(anchor.id).push(el);
    }
  });

  const slotMap = new Map();
  entries.filter((e) => e.startTime).forEach((e) => {
    const key = formatTime(e.startTime);
    if (!slotMap.has(key)) slotMap.set(key, []);
    slotMap.get(key).push(e);
  });
  const slots = [...slotMap.entries()]
    .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
    .map(([time, list]) => ({ time, entries: list, after: list.flatMap((e) => after.get(e.id) || []) }));

  const roomRank = new Map((program?.rooms || []).map((r, i) => [r.id, i]));
  const untimedMap = new Map();
  entries.filter((e) => !e.startTime).forEach((e) => {
    const key = e.roomId ?? null;
    if (!untimedMap.has(key)) untimedMap.set(key, []);
    untimedMap.get(key).push({ entry: e, after: after.get(e.id) || [] });
  });
  const untimed = [...untimedMap.entries()]
    .map(([roomId, items]) => ({ roomId, items }))
    .sort((a, b) => (a.roomId == null ? 1 : b.roomId == null ? -1 : (roomRank.get(a.roomId) ?? 0) - (roomRank.get(b.roomId) ?? 0)));

  const removed = all.filter((n) => n.removedAt != null).sort(bySort);

  return { before, slots, untimed, removed };
};

// The days that hold something, in the backend's order (ProgramView.days).
export const daysOf = (program) => (program?.days || []).filter(
  (day) => nodesOfDay(program, day.id).length > 0,
);

export const dayLabel = (intl, language, day) => {
  const label = pick(language, day.labelDe, day.labelEn);
  if (label) return label;
  const date = localDate(day.day);
  return date ? intl.formatDate(date, { weekday: 'long', day: 'numeric', month: 'long' }) : '';
};

// The room of a top-level entry, and whether it sits in another venue than the event's default one.
export const placeOf = (program, roomId) => {
  const room = (program?.rooms || []).find((r) => r.id === roomId) || null;
  const venue = room ? (program?.venues || []).find((v) => v.id === room.venueId) || null : null;
  const otherVenue = venue && program?.event?.defaultVenueId != null && venue.id !== program.event.defaultVenueId ? venue : null;
  return { room, venue: otherVenue };
};

export const roomName = (language, room) => (room ? pick(language, room.nameDe, room.nameEn) : '');
export const venueText = (venue) => (venue ? [venue.name, venue.city].filter(Boolean).join(', ') : '');

export const nodeTitle = (node, language) => pick(language, node.titleDe, node.titleEn);
export const nodeLabel = (node, language) => pick(language, node.labelDe, node.labelEn);

// The one-line address of an entry for the panels, the People table and the "move to" picker.
export const entryPath = (program, entry, language) => {
  const day = (program?.days || []).find((d) => d.id === entry.eventDayId);
  const parts = [];
  if (day) parts.push(pick(language, day.labelDe, day.labelEn) || day.day);
  if (entry.startTime) parts.push(timeRange(entry.startTime, entry.endTime));
  const { room } = placeOf(program, entry.roomId);
  if (room) parts.push(roomName(language, room));
  return parts.join(' · ');
};

// What is printed where the name would be while no name is shown (ProgramView.placeholderLabel
// defaults to "TBA").
export const spotLabel = (a) => a.personName || a.placeholderLabel || null;

// ---------------------------------------------------------------- the People area

// A date-only value ("2026-11-05") is read as the local day, never as UTC midnight.
const asDate = (value) => (/^\d{4}-\d{2}-\d{2}$/.test(String(value)) ? localDate(value) : new Date(value));

export const formatDay = (intl, value) => (value
  ? intl.formatDate(asDate(value), { day: '2-digit', month: '2-digit', year: 'numeric' })
  : null);

export const formatDateTime = (intl, value) => (value
  ? intl.formatDate(asDate(value), { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
  : null);

// One row per person at this event: who they are (EventPeopleService.Row) plus every spot they hold,
// read from the program (live ones only - a removed spot is not part of the event any more).
export const peopleRows = (program, people) => {
  const byPerson = new Map();
  walkEntries(program, (entry) => {
    (entry.assignments || []).forEach((a) => {
      if (a.removedAt || a.personId == null) return;
      if (!byPerson.has(a.personId)) byPerson.set(a.personId, []);
      byPerson.get(a.personId).push({
        assignmentId: a.id, entryId: entry.id, entry, role: a.role, status: a.status, openSpot: isOpenSpot(a),
        invitedBy: invitedByOf(a),
      });
    });
  });
  return (people || []).map((person) => ({
    ...person,
    spots: (byPerson.get(person.personId) || []).sort((x, y) => (x.entry.sortOrder ?? 0) - (y.entry.sortOrder ?? 0)),
  }));
};

// ---------------------------------------------------------------- one person's reminders

// Reminders are per person and event (ReminderService), not per spot: one email lists every open,
// non-stopped invitation of that person at the event. These read the reminder state the API returns
// with the person (EventPeopleService.Row.reminders) - reported, never derived twice.
export const personById = (people, personId) => (people || []).find((p) => p.personId === personId) || null;

export const personReminders = (person) => (person?.reminders || {
  openInvitations: 0, stoppedInvitations: 0, remindersStopped: false, remindersSent: null, maxReminders: 0,
  daysBetweenReminders: 0,
});

// The emails around a spot's open invitation (ReminderService.Anchor via the person row; reminders
// are per person and event, so the dates are the person's at this event): when this spot was asked,
// the newest email of either kind, whether that was a reminder, how many reminders went out of the
// maximum, and the next automatic one (none once the maximum is reached).
export const mailStateOf = (assignment, person) => {
  const open = openInvitationOf(assignment);
  if (!open) return null;
  const r = personReminders(person);
  const askedAt = open.askedAt || null;
  // the person's newest email at the event, but never older than this spot's own invitation
  const lastSentAt = r.lastSentAt && (!askedAt || new Date(r.lastSentAt) > new Date(askedAt)) ? r.lastSentAt : askedAt;
  const reminder = !!(r.remindersSent && lastSentAt !== askedAt);
  return {
    askedAt, lastSentAt, reminder, remindersSent: r.remindersSent || 0, maxReminders: r.maxReminders || 0,
    daysBetweenReminders: r.daysBetweenReminders || 0,
    nextReminder: r.nextAutomaticReminder || null, stopped: !!r.remindersStopped,
  };
};

// Nothing open to remind: everything is answered, or every open invitation has its reminders stopped
// (ReminderService: without an anchor there is nothing to remind, the backend answers 409).
export const canRemindPerson = (person) => {
  const r = personReminders(person);
  return r.openInvitations > 0 && !r.remindersStopped;
};
export const canStopReminders = (person) => {
  const r = personReminders(person);
  return r.openInvitations > 0 && !r.remindersStopped;
};
export const canResumeReminders = (person) => !!person && personReminders(person).remindersStopped;

// An invitation needs at least one spot the backend would accept (InvitationController: an open spot,
// a CONFIRMED or a removed assignment is refused). It needs a contact address too, but a person without
// one can still be invited from here: the invite dialog asks for the address first.
export const canInvitePerson = (row) => (row?.spots || []).some((spot) => INVITE_FROM.includes(spot.status));

// The open invitations of that person at this event, counted in the program itself - what the
// reminder dialog says ("reminds X about all their open invitations").
export const openInvitationsOf = (program, personId) => {
  let count = 0;
  walkEntries(program, (entry) => {
    (entry.assignments || []).forEach((a) => {
      if (!a.removedAt && a.personId === personId && a.status === 'INVITED') count += 1;
    });
  });
  return count;
};


// ------------------------------------------------------------------ the change route's bodies

const blankToNull = (value) => {
  const text = typeof value === 'string' ? value.trim() : value;
  return text === '' || text == null ? null : text;
};

const numberOrNull = (value) => (value === '' || value == null ? null : Number(value));

// ProgramRequests.EntryContent, built from the edit form (blank text clears the field, as the backend
// does with blank text). Also the program screen's edit dialog and the container's change call.
export const entryContentOf = (fields = {}) => ({
  kind: fields.kind,
  seriesLabelDe: blankToNull(fields.seriesLabelDe),
  seriesLabelEn: blankToNull(fields.seriesLabelEn),
  focusDe: blankToNull(fields.focusDe),
  focusEn: blankToNull(fields.focusEn),
  labelDe: blankToNull(fields.labelDe),
  labelEn: blankToNull(fields.labelEn),
  titleDe: blankToNull(fields.titleDe),
  titleEn: blankToNull(fields.titleEn),
  subtitleDe: blankToNull(fields.subtitleDe),
  subtitleEn: blankToNull(fields.subtitleEn),
  startTime: blankToNull(fields.startTime),
  endTime: blankToNull(fields.endTime),
  discussionMinutes: numberOrNull(fields.discussionMinutes),
  asksForSlides: typeof fields.asksForSlides === 'boolean' ? fields.asksForSlides : null,
  visibility: fields.visibility || null,
  internalNotes: blankToNull(fields.internalNotes),
});

// ProgramRequests.EntryChange (without the notice and the email): the Edit dialog's content and room,
// or a move (the Up/Down body, or the Move-to fields). The change route runs the same code as the
// plain PUT/the move, so the room travels with the content and null clears it.
export const entryChangeOf = (target, fields = {}) => {
  if (target?.kind === 'editEntry') {
    return { entry: entryContentOf(fields), roomId: numberOrNull(fields.roomId) };
  }
  if (target?.body) return { move: target.body };
  const parentId = numberOrNull(fields.parentId);
  return {
    move: parentId != null
      ? { parentId, eventDayId: null, roomId: null, position: null }
      : { parentId: null, eventDayId: numberOrNull(fields.eventDayId), roomId: numberOrNull(fields.roomId), position: null },
  };
};

// ProgramRequests.CreateEntry, the `create` of a CreateChange (Add block and Add child): a child
// under the dialog's entry, or a block on the top level of a day. Blank times stay null; a child
// without times inherits its block's time exactly as before.
export const createOf = (target, fields = {}) => {
  const entry = {
    kind: fields.kind,
    titleDe: blankToNull(fields.titleDe),
    titleEn: blankToNull(fields.titleEn),
    startTime: blankToNull(fields.startTime),
    endTime: blankToNull(fields.endTime),
  };
  return target?.kind === 'addChild'
    ? { parentId: target.entryId, position: null, entry }
    : {
      parentId: null,
      eventDayId: numberOrNull(fields.eventDayId),
      roomId: numberOrNull(fields.roomId),
      position: null,
      entry,
    };
};

// The `notify` of a cancellation or withdrawal: the rows of the dialog whose box is ticked and that
// have an address. A removal keeps the choice in its fixed change, so it survives the tell step.
export const notifyOf = (emails) => {
  const sending = (emails || []).filter((email) => email.send && email.personId != null);
  return {
    send: sending.length > 0,
    messages: sending.map((email) => ({
      personId: email.personId,
      subject: email.subject || null,
      body: email.html || null,
    })),
  };
};
